/**
 * Custom hook for converting successful ventures to projects
 * Handles the conversion process with cache invalidation and notifications
 *
 * Requirements:
 * - 8.1: Verify venture status is 'successful'
 * - 8.2: Create new project with venture data
 * - 8.3: Update venture status to 'converted'
 * - 8.4: Add all backers as workspace members
 * - 8.5: Link project to venture via venture_id
 */
import { mutate } from "swr";
import { useServerState, createHeaders, handleApiError } from "@project-mgmt/api-client";
import { invalidateVentureCache, invalidateVenturesCache } from "./useVentureActions";
/**
 * Hook to create a conversion mutation
 * Requirement 8.1-8.5: Convert successful venture to project
 *
 * @param ventureId - The venture ID to convert
 * @param workspaceId - The workspace ID for authorization
 * @returns Mutation hook for converting ventures
 */
export function useConvertToProjectMutation(ventureId, workspaceId) {
    return useServerState({
        key: ["ventures:convert", ventureId, workspaceId],
        fetcher: async (_, signal) => {
            try {
                const response = await fetch(`/api/ventures/${ventureId}/convert`, {
                    method: "POST",
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to convert venture to project");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to convert venture to project:", error);
        },
    });
}
/**
 * Helper function to invalidate project-related cache
 * Invalidates project lists after conversion
 *
 * @param workspaceId - The workspace ID to invalidate cache for
 */
export function invalidateProjectsCache(workspaceId) {
    // Invalidate all projects list caches for this workspace
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            // Match projects endpoint for this workspace
            return parsedKey[0] === "projects" && parsedKey[1] === workspaceId;
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
}
/**
 * Hook for converting a successful venture to a project
 * Provides conversion operation with cache management and notifications
 *
 * @param ventureId - The venture ID to convert
 * @param workspaceId - The workspace ID for authorization
 * @param notify - Optional notification adapter for success/error messages
 * @returns Object with conversion function, loading state, and result data
 *
 * @example
 * ```tsx
 * const { convertToProject, isConverting, result } = useConvertToProject(
 *   ventureId,
 *   workspaceId,
 *   {
 *     success: (msg) => toast.success(msg),
 *     error: (msg) => toast.error(msg),
 *   }
 * );
 *
 * // Convert the venture
 * const { success, projectId } = await convertToProject();
 * if (success) {
 *   navigate(`/projects/${projectId}`);
 * }
 * ```
 */
export function useConvertToProject(ventureId, workspaceId, notify) {
    const convertMutation = useConvertToProjectMutation(ventureId, workspaceId);
    /**
     * Convert the venture to a project
     * Requirement 8.1-8.5: Full conversion process
     */
    const convertToProject = async () => {
        try {
            // Execute the conversion
            const result = await convertMutation.trigger(undefined);
            // Invalidate all relevant caches after successful conversion
            invalidateVentureCache(ventureId);
            invalidateVenturesCache(workspaceId);
            invalidateProjectsCache(workspaceId);
            // Show success notification with details
            const message = `Venture converted successfully! ${result.addedMembers} backer(s) added as workspace members.`;
            notify?.success(message);
            return {
                success: true,
                projectId: result.project.id,
                data: result,
            };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Failed to convert venture to project";
            // Show error notification
            notify?.error(message);
            return {
                success: false,
                error: message,
            };
        }
    };
    return {
        convertToProject,
        isConverting: convertMutation.isLoading,
        result: convertMutation.data,
    };
}
